// Open parks (spec FE-04): parks that want strangers, fullest first, refreshed while the menu is
// on screen. Each row is the design system's ParkCard; its Join takes the seat by code. The Stake
// tables tab lists Lagos Life matches still filling; joining or opening one goes through Lagos
// Life's own fare sheet, then our server binds the match to its stake park.
import { isFramed, vatar } from '@wahala/vatar-client';
import { useEffect, useState } from 'react';
import { api, type JoinTicket, type OpenPark, type StakeTable } from '../lib/api';
import { bindStake, leaveUnstarted, stakeAllowed } from '../lib/stake';
import { type StakeGame, stakeGame } from '../lib/stakeFill';
import { useUi } from '../store/ui';
import { S } from '../strings';
import { Html, W } from './wahala';

const REFRESH_MS = 5_000;

type Tab = 'free' | 'stakes';

/** Load a list now and every few seconds while the page is on screen. */
function usePolled<T>(load: (() => Promise<T>) | null): T | null {
  const [v, setV] = useState<T | null>(null);
  useEffect(() => {
    if (!load) return;
    let stop = false;
    const run = () => {
      if (document.hidden) return;
      load()
        .then((r) => !stop && setV(r))
        .catch(() => {});
    };
    run();
    const t = setInterval(run, REFRESH_MS);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [load]);
  return v;
}

const loadParks = () => api.openParks().then((r) => r.parks);
const loadTables = () => api.stakes().then((r) => r.tables);
const count = (n: unknown) => Math.max(0, Math.trunc(Number(n)) || 0);

export function OpenParks({
  busy,
  onJoin,
  onStake,
  freeOnly,
  stakesOnly,
  preset,
}: {
  busy: boolean;
  onJoin: (code: string) => void;
  onStake: (f: () => Promise<JoinTicket>) => void;
  /** One list only, no tabs: a game type's sheet shows its free parks, the stake tile its tables. */
  freeOnly?: boolean;
  stakesOnly?: boolean;
  /** Only parks of this game type. */
  preset?: string;
}) {
  const config = useUi((s) => s.config);
  const [tab, setTab] = useState<Tab>(stakesOnly ? 'stakes' : 'free');
  // stake tables need the Lagos Life frame and a server whose platform has them (PF-18)
  const stakesHere = isFramed() && config?.lagosLife === true && config.platforms?.stakes !== false;

  return (
    <section className="open-parks" aria-labelledby="open-parks-title">
      <h2 id="open-parks-title" className="label">
        {stakesOnly ? S.stakes.listTitle : S.parks.title}
      </h2>
      {!freeOnly && !stakesOnly && (
        <div className="chips open-parks__tabs" role="tablist" aria-label={S.parks.title}>
          {(['free', 'stakes'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`chip${tab === t ? ' is-on' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'free' ? S.parks.free : S.parks.stakes}
            </button>
          ))}
        </div>
      )}
      {tab === 'free' && <FreeParks busy={busy} onJoin={onJoin} {...(preset ? { preset } : {})} />}
      {tab === 'stakes' &&
        (stakesHere ? (
          <StakeTables
            busy={busy}
            tiers={config?.stakeTiers ?? []}
            // a 2-seat table plays Race: offered only while the park office has Race tables on
            race={config?.gameTypes?.find((t) => t.id === 'race')?.stake === true}
            onStake={onStake}
          />
        ) : (
          <p className="muted small">{S.stakes.outside}</p>
        ))}
    </section>
  );
}

function FreeParks({ busy, onJoin, preset }: { busy: boolean; onJoin: (code: string) => void; preset?: string }) {
  const all = usePolled<OpenPark[]>(loadParks);
  const parks = all && preset ? all.filter((p) => p.preset === preset) : all;
  if (parks === null) return <p className="muted small">{S.parks.loading}</p>;
  if (parks.length === 0) return <p className="muted small">{S.parks.none}</p>;
  return (
    <ul className="open-parks__list">
      {parks.map((p) => (
        <li key={p.code}>
          <Html
            // parkCard escapes the name and the rules; the counts go in raw, so force them to numbers
            html={W().parkCard({
              name: p.name ?? S.parks.name(p.code),
              map: S.presets[p.preset] ?? p.preset,
              seats: count(p.capacity),
              seated: count(p.count),
            })}
            onClick={(e) => {
              if (!busy && (e.target as HTMLElement).closest('button:not([disabled])')) onJoin(p.code);
            }}
          />
        </li>
      ))}
    </ul>
  );
}

function StakeTables({
  busy,
  tiers,
  race,
  onStake,
}: {
  busy: boolean;
  tiers: number[];
  race: boolean;
  onStake: (f: () => Promise<JoinTicket>) => void;
}) {
  const tables = usePolled<StakeTable[]>(loadTables);
  const [picking, setPicking] = useState(false);
  const [tier, setTier] = useState<number | null>(null);
  const [seats, setSeats] = useState<2 | 4>(4);
  const game: StakeGame = stakeGame(seats);

  // a table not started yet (an old one) is left first: one match at a time
  const join = (code: string, stake: number) =>
    onStake(async () => {
      await stakeAllowed(stake);
      await leaveUnstarted();
      return bindStake(await vatar.match.join(code));
    });
  const open = (stake: number) =>
    onStake(async () => {
      await stakeAllowed(stake);
      await leaveUnstarted();
      return bindStake(await vatar.match.create(stake, seats));
    });

  return (
    <>
      <p className="muted small">{S.stakes.note}</p>
      {picking ? (
        <div className="stack">
          {race && (
            <fieldset className="stake-seats">
              <legend className="label">{S.stakes.seatsTitle}</legend>
              {([2, 4] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`stake-seats__pick${seats === n ? ' is-on' : ''}`}
                  aria-pressed={seats === n}
                  disabled={busy}
                  onClick={() => setSeats(n)}
                >
                  <strong>{n === 2 ? S.stakes.seatsRace : S.stakes.seatsPark}</strong>
                  <span>{n === 2 ? S.stakes.seatsRaceGame : S.stakes.seatsParkGame}</span>
                </button>
              ))}
            </fieldset>
          )}
          <Html
            // the tiers come from our own config; tapping one picks it, the fare button opens the sheet
            html={W().stakeTierPicker({
              tiers: tiers.map(count).filter((t) => t > 0),
              ...(tier !== null ? { selected: tier } : {}),
              seats,
              aside: S.stakes.seatCount(seats),
              title: S.stakes.title,
              disabled: busy,
            })}
            onClick={(e) => {
              const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-fare]:not([disabled])');
              if (!b) return;
              const fare = count(b.dataset.fare);
              if (b.classList.contains('wh-tier')) setTier(fare);
              else if (fare > 0 && tiers.includes(fare)) open(fare);
            }}
          />
          <button
            type="button"
            className="wh-btn wh-btn--quiet wh-btn--md wh-btn--block"
            onClick={() => {
              setPicking(false);
              setTier(null);
            }}
          >
            {S.stakes.cancel}
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="wh-btn wh-btn--quiet wh-btn--md wh-btn--block"
          disabled={busy || tiers.length === 0}
          onClick={() => setPicking(true)}
        >
          {S.stakes.open}
        </button>
      )}
      {tables === null && <p className="muted small">{S.stakes.loading}</p>}
      {tables?.length === 0 && <p className="muted small">{S.stakes.none}</p>}
      {tables && tables.length > 0 && (
        <ul className="open-parks__list">
          {tables.map((t) => (
            <li key={t.matchId}>
              <Html
                html={W().parkCard({
                  name: S.stakes.name(t.code),
                  map: S.stakes.gameName(t.game ?? stakeGame(count(t.seats))),
                  fare: count(t.stake),
                  seats: count(t.seats),
                  seated: count(t.seated),
                })}
                onClick={(e) => {
                  if (!busy && (e.target as HTMLElement).closest('button:not([disabled])')) join(t.code, t.stake);
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
